import { useCallback, useEffect, useState } from "react";

/**
 * Reads the current session from the server on every mount.
 *
 * There is deliberately no client-side session cache and no decoding of anything the
 * browser holds. The session cookie is `HttpOnly`, so the browser cannot read it, and
 * that is the point: the only honest answer to "am I signed in" comes from the server
 * asking its own database. A cached "yes" would keep claiming yes after a revocation.
 *
 * `unconfigured` is a distinct state, not an error. An environment without a verified
 * OAuth client answers 503, and a page that offered a sign-in button there would be
 * offering something guaranteed to fail.
 */

export const SESSION_PATH = "/api/v1/auth/session";
export const SIGN_IN_PATH = "/api/v1/auth/google/start";
export const LOGOUT_PATH = "/api/v1/auth/logout";
export const CSRF_COOKIE = "__Host-lc_csrf";

export interface SignedInSession {
  readonly leagueCommandUserId: string;
  readonly sessionFingerprint: string;
  readonly sessionVersion: number;
  readonly expiresAt: string;
}

export type SessionState =
  | { readonly status: "loading" }
  | { readonly status: "anonymous" }
  | { readonly status: "unconfigured" }
  | { readonly status: "error" }
  | { readonly status: "signed-in"; readonly session: SignedInSession };

export interface SessionHandle {
  readonly state: SessionState;
  readonly reload: () => void;
}

export const readCsrfToken = (): string | null => {
  const match = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${CSRF_COOKIE}=`));
  return match === undefined ? null : match.slice(CSRF_COOKIE.length + 1);
};

/**
 * Build the sign-in URL, returning to where the user already is.
 *
 * Only `pathname` is passed. The server validates the destination and refuses anything
 * that could redirect off-origin, but handing it a bare path means there is nothing for
 * that check to have to reject in the first place.
 */
export const signInHref = (pathname: string): string =>
  pathname === "/" || pathname === ""
    ? SIGN_IN_PATH
    : `${SIGN_IN_PATH}?next=${encodeURIComponent(pathname)}`;

export const useSession = (): SessionHandle => {
  const [state, setState] = useState<SessionState>({ status: "loading" });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading" });

    void fetch(SESSION_PATH, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    })
      .then(async (response) => {
        if (response.status === 401) {
          await response.text();
          setState({ status: "anonymous" });
          return;
        }
        if (response.status === 503) {
          await response.text();
          setState({ status: "unconfigured" });
          return;
        }
        if (!response.ok) {
          throw new Error("Session request failed.");
        }
        const payload = (await response.json()) as SignedInSession;
        setState({ status: "signed-in", session: payload });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setState({ status: "error" });
      });

    return () => controller.abort();
  }, [reloadKey]);

  const reload = useCallback(() => setReloadKey((value) => value + 1), []);

  return { state, reload };
};
